'use client';

import { c as _c } from "react/compiler-runtime";
import { formatFundingPreset, withdrawalArtwork } from '@morfi/onramp-sdk';
import type { useFundingCurrency, useWithdrawalAmount, useWithdrawalCheckout } from '@morfi/onramp-sdk/react';
import { useLocale, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedButton } from '@/components/ui/animated-button';
import { useFaucetClaim } from '@/hooks/faucet/use-faucet-claim';
import { getActiveNetwork } from '@/lib/network/active';
import { FundingAmount } from '../checkout/amount/funding-amount';
type Flow = ReturnType<typeof useWithdrawalCheckout>;
export function WithdrawAmountScreen(t0) {
  const $ = _c(50);
  const {
    flow,
    amount,
    currency,
    context,
    value,
    setValue,
    expired,
    retryRequest,
    run,
    fees
  } = t0;
  const t = useTranslations("onramp");
  const locale = useLocale();
  const faucetText = useTranslations("portfolio.faucet_banner");
  const faucet = useFaucetClaim("portfolio_banner");
  const retryCheckout = Boolean(expired || flow.context.isError || flow.activeId && flow.operation.isError || flow.resumeError);
  let t1;
  if ($[0] !== flow.method || $[1] !== t) {
    t1 = flow.method ? t(flow.method) : t("withdraw_title");
    $[0] = flow.method;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== flow.method) {
    t2 = flow.method ? <OptimizedImage nsfw={false} variant="thumbnail" preview={false} loadReveal="none" rounded="" imageClassName="object-contain" src={`/assets/onramp/method-${withdrawalArtwork[flow.method]}.png`} alt="" size="size-5" className="bg-transparent" /> : undefined;
    $[3] = flow.method;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== amount.maximumUnits || $[6] !== currency.currency || $[7] !== currency.decimals || $[8] !== locale || $[9] !== t) {
    t3 = units => units === amount.maximumUnits ? t("withdraw_max") : formatFundingPreset(units, currency.decimals, currency.currency, locale);
    $[5] = amount.maximumUnits;
    $[6] = currency.currency;
    $[7] = currency.decimals;
    $[8] = locale;
    $[9] = t;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== amount.amountD6 || $[12] !== amount.boundary || $[13] !== amount.loading || $[14] !== amount.quote || $[15] !== context || $[16] !== currency.status || $[17] !== expired || $[18] !== faucet || $[19] !== faucetText || $[20] !== flow.commands.isError || $[21] !== flow.commands.isPending || $[22] !== flow.context || $[23] !== retryCheckout || $[24] !== retryRequest || $[25] !== run || $[26] !== t) {
    t4 = retryCheckout || amount.quote.isError || flow.commands.isError ? <AnimatedButton className="h-11 w-full" loading={!retryCheckout && amount.loading || flow.commands.isPending} disabled={flow.commands.isPending || !retryCheckout && (amount.loading || !amount.amountD6 || Boolean(amount.boundary) || currency.status !== "ready")} onClick={() => {
      if (retryCheckout) {
        retryRequest();
      } else {
        if (amount.quote.isError) {
          amount.quote.refetch();
        } else {
          if (amount.amountD6 && !amount.boundary) {
            run({
              kind: "quote",
              amountD6: amount.amountD6
            });
          }
        }
      }
    }}>{t(expired ? "sign_in" : "retry")}</AnimatedButton> : context.balanceD6 === "0" && getActiveNetwork() !== "mainnet" ? <AnimatedButton className="h-11 w-full" loading={faucet.isPending} onClick={() => faucet.claim(context.source, {
      onClaimed: () => {
        flow.context.refetch();
      }
    })}>{faucetText("title")}</AnimatedButton> : null;
    $[11] = amount.amountD6;
    $[12] = amount.boundary;
    $[13] = amount.loading;
    $[14] = amount.quote;
    $[15] = context;
    $[16] = currency.status;
    $[17] = expired;
    $[18] = faucet;
    $[19] = faucetText;
    $[20] = flow.commands.isError;
    $[21] = flow.commands.isPending;
    $[22] = flow.context;
    $[23] = retryCheckout;
    $[24] = retryRequest;
    $[25] = run;
    $[26] = t;
    $[27] = t4;
  } else {
    t4 = $[27];
  }
  const t5 = !flow.commands.isPending && currency.status === "ready" && (flow.method === "manual_withdraw" || Boolean(amount.estimate));
  let t6;
  if ($[28] !== amount.amountD6 || $[29] !== amount.boundary || $[30] !== run) {
    t6 = () => {
      if (amount.amountD6 && !amount.boundary) {
        run({
          kind: "quote",
          amountD6: amount.amountD6
        });
      }
    };
    $[28] = amount.amountD6;
    $[29] = amount.boundary;
    $[30] = run;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  let t7;
  if ($[32] !== amount.boundary || $[33] !== amount.estimate || $[34] !== amount.loading || $[35] !== amount.presets || $[36] !== currency.currency || $[37] !== currency.decimals || $[38] !== fees || $[39] !== flow.back || $[40] !== flow.commands.isPending || $[41] !== setValue || $[42] !== t1 || $[43] !== t2 || $[44] !== t3 || $[45] !== t4 || $[46] !== t5 || $[47] !== t6 || $[48] !== value) {
    t7 = <FundingAmount methodLabel={t1} methodArtwork={t2} currency={currency.currency} decimals={currency.decimals} presets={amount.presets} presetLabel={t3} estimate={amount.estimate} quoteLoading={amount.loading} primaryAction={t4} value={value} onValueChange={setValue} canContinue={t5} pending={flow.commands.isPending} amountBoundary={amount.boundary} boundaryFeedback="snackbar" feeDisclosure={fees} onChangeMethod={flow.back} onContinue={t6} />;
    $[32] = amount.boundary;
    $[33] = amount.estimate;
    $[34] = amount.loading;
    $[35] = amount.presets;
    $[36] = currency.currency;
    $[37] = currency.decimals;
    $[38] = fees;
    $[39] = flow.back;
    $[40] = flow.commands.isPending;
    $[41] = setValue;
    $[42] = t1;
    $[43] = t2;
    $[44] = t3;
    $[45] = t4;
    $[46] = t5;
    $[47] = t6;
    $[48] = value;
    $[49] = t7;
  } else {
    t7 = $[49];
  }
  return t7;
}